import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Download, 
  FileSpreadsheet, 
  Trash2,
  Building2,
  Calendar,
  MapPin,
  Truck,
  FileText,
  User,
  Mail,
  CheckCircle
} from "lucide-react";

const API = '/api';

export default function DetalheEncomenda() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [encomenda, setEncomenda] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEncomenda = async () => {
      try {
        const response = await axios.get(`${API}/encomendas/${id}`);
        setEncomenda(response.data);
      } catch (error) {
        toast.error("Erro ao carregar encomenda");
        navigate("/encomendas");
      } finally {
        setLoading(false);
      }
    };

    fetchEncomenda();
  }, [id, navigate]);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/encomendas/${id}`);
      toast.success("Encomenda eliminada");
      navigate("/encomendas");
    } catch (error) {
      toast.error("Erro ao eliminar encomenda");
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/encomendas/${id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `encomenda_${id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF gerado com sucesso");
    } catch (error) {
      toast.error("Erro ao gerar PDF");
    }
  };

  const downloadExcel = async () => {
    try {
      const response = await axios.get(`${API}/encomendas/${id}/excel`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `encomenda_${id.slice(0, 8)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Excel gerado com sucesso");
    } catch (error) {
      toast.error("Erro ao gerar Excel");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!encomenda) return null;

  const itensComQuantidade = encomenda.itens.filter(i => i.quantidade > 0 || i.oferta > 0);

  return (
    <div className="space-y-6" data-testid="order-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/encomendas">
            <Button variant="ghost" size="icon" className="text-slate-500">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="font-heading font-extrabold text-2xl text-slate-900">
              Detalhes da Encomenda
            </h1>
            <p className="text-slate-500 text-sm">
              ID: {encomenda.id.slice(0, 8)}...
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={downloadPDF}
            className="gap-2"
            data-testid="download-pdf-btn"
          >
            <Download className="w-4 h-4" />
            PDF
          </Button>
          <Button
            variant="outline"
            onClick={downloadExcel}
            className="gap-2"
            data-testid="download-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Excel
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="gap-2" data-testid="delete-order-btn">
                <Trash2 className="w-4 h-4" />
                Eliminar
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminar Encomenda?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta ação não pode ser revertida. A encomenda será permanentemente eliminada.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Eliminar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                  Farmácia
                </p>
                <p className="font-semibold text-slate-900">{encomenda.farmacia_nome}</p>
                <p className="text-sm text-slate-500">NIF: {encomenda.farmacia_nif}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-orange-50 rounded-lg">
                <Truck className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                  Armazenista
                </p>
                <p className="font-semibold text-slate-900">{encomenda.armazenista}</p>
                <p className="text-sm text-slate-500">Transfer</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                  Período
                </p>
                <p className="font-semibold text-slate-900">{encomenda.mes} {encomenda.ano}</p>
                <p className="text-sm text-slate-500">
                  Emissão: {new Date(encomenda.data_emissao).toLocaleDateString('pt-PT')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-violet-50 rounded-lg">
                <Mail className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                  Email
                </p>
                {encomenda.email_enviado ? (
                  <div className="flex items-center gap-1 text-blue-600">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-semibold">Enviado</span>
                  </div>
                ) : (
                  <span className="font-semibold text-slate-500">Pendente</span>
                )}
                {encomenda.criado_por_nome && (
                  <p className="text-sm text-slate-500">Por: {encomenda.criado_por_nome}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Morada */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-slate-100 rounded-lg">
              <MapPin className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                Morada de Entrega
              </p>
              <p className="text-slate-700">{encomenda.farmacia_morada}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Observações */}
      {encomenda.observacoes && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-50 rounded-lg">
                <FileText className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                  Observações / Anotações
                </p>
                <p className="text-slate-700 whitespace-pre-wrap">{encomenda.observacoes}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Products Table */}
      <Card>
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="font-heading text-lg">Produtos ({itensComQuantidade.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="font-bold text-slate-700">Código</TableHead>
                  <TableHead className="font-bold text-slate-700">Descrição</TableHead>
                  <TableHead className="font-bold text-slate-700 text-center">Quantidade</TableHead>
                  <TableHead className="font-bold text-slate-700 text-center">Oferta</TableHead>
                  <TableHead className="font-bold text-slate-700 text-right">PVA</TableHead>
                  <TableHead className="font-bold text-slate-700 text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itensComQuantidade.map((item, index) => (
                  <TableRow key={index} className="table-row-hover">
                    <TableCell className="font-mono text-sm">{item.codigo}</TableCell>
                    <TableCell className="font-medium">{item.descricao}</TableCell>
                    <TableCell className="text-center">{item.quantidade}</TableCell>
                    <TableCell className="text-center">
                      {item.oferta > 0 && (
                        <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                          {item.oferta}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">€{item.pva.toFixed(2)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">
                      €{item.total.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Total */}
          <div className="border-t-2 border-blue-600 p-6 bg-blue-50/50">
            <div className="flex items-center justify-between">
              <span className="font-heading font-bold text-lg text-slate-800">
                Total da Encomenda
              </span>
              <span className="font-mono font-bold text-2xl text-blue-700">
                €{encomenda.total_geral.toFixed(2)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
